import {_} from 'lodash';
import * as ko from 'knockout';
import {viewModel as SearchBasic} from '../../object/search.basic';
import {DEFAULT_SEARCH_PAGE, MAX_RESULTS_PER_PAGE, ObjectTypeId} from '../../../constants';
import {
    IObjectData,
    IOverride,
    IResponse,
    IRouteDefinitionParams,
    IRule,
    ISearchObjectParams,
    ITableColumn,
    TSyncfield,
} from '../../../index';
import {createErrorMessage, getObjectDisplayName, getRawSyncFieldValue} from '../../../util/misc';
import {TSyncfield_User} from '../../../constants/Syncfields';
import PromiseRequest from '../../../req/PromiseRequest';
import {global} from '../../../appContext';
import {getSearchObjectParams} from '../../../util/request';
import {nsLanguage} from '../../../util/nsLanguage';
import {nsRuleset} from '../../ruleset/nsRuleset';
import {nsSyncFields} from '../../../req/nsSyncFields';
import {memoryStore} from '../../../util/memorystore';
import {storeObservable} from '../../../util/sessionstore';
import {nsGroupMemberOverride} from './nsGroupMemberOverride';
import {nsGroupMembers} from './nsGroupMembers';
import {nsSearch} from '../../../constants/Language';
import {
    buildDepartmentBatchRule,
    buildDepartmentLookupMap,
    buildManagerBatchRules,
    buildManagerLookupMap,
    collectDepartmentOrgUnitNumbers,
    collectManagerReferencesByImportFormat,
    getDepartmentLookupSyncfieldNames,
    getManagerLookupSyncfieldNames,
    MEMBER_CURRENT_TAB_USER_TUPLE_NAMES,
    MEMBER_ROW_SYNCFIELD_NAMES,
} from './nsGroupMemberLookups';
import {GroupMemberRowVm} from './memberRow';
import getSyncfieldIdsFromNames = nsSyncFields.getSyncfieldIdsFromNames;
import convertTuplesToObjectData = nsSyncFields.convertTuplesToObjectData;
import TObjectData_basic = nsSyncFields.TObjectData_basic;
import canMutateMembershipOverride = nsGroupMemberOverride.canMutateMembershipOverride;
import filterOverridesForMembersTab = nsGroupMemberOverride.filterOverridesForMembersTab;
import toLocaleString = nsLanguage.toLocaleString;
import findOverrideForUser = nsGroupMemberOverride.findOverrideForUser;
import userMatchesMembersTab = nsGroupMemberOverride.userMatchesMembersTab;
import TGroupMembersTab = nsGroupMemberOverride.TGroupMembersTab;
import getMembershipOverrideAction = nsGroupMemberOverride.getMembershipOverrideAction;
import runMembershipOverrideDelete = nsGroupMemberOverride.runMembershipOverrideDelete;
import runMembershipOverrideRestore = nsGroupMemberOverride.runMembershipOverrideRestore;
import runMembershipRemoveMember = nsGroupMemberOverride.runMembershipRemoveMember;
import showCancelPendingDeletionConfirm = nsGroupMemberOverride.showCancelPendingDeletionConfirm;
import showMembershipOverrideActionConfirm = nsGroupMemberOverride.showMembershipOverrideActionConfirm;
import IMembershipOverrideMutationCallbacks = nsGroupMemberOverride.IMembershipOverrideMutationCallbacks;
import {nsOverride} from '../../override/nsOverride';
import isOverridePendingDeletion = nsOverride.isOverridePendingDeletion;
import generateRule = nsRuleset.generateRule;
import generateRuleset = nsRuleset.generateRuleset;

/** Default `show all` when a tab has no stored preference yet. */
const SHOW_ALL_TAB_DEFAULTS: Record<TGroupMembersTab, boolean> = {
    current: false,
    pending: true,
    added: true,
    removed: true,
};

/** Object search for users who are members of the current group route. */
export class GroupMemberSearch extends SearchBasic {
    readonly resultsPerPage = storeObservable('num.search.results');
    override objecttypeid: ObjectTypeId = ObjectTypeId.User;
    table: ITableColumn[] = [
        {key: 'GivenName', sort: true},
        {key: 'Surname', sort: true},
        {key: 'EmployeeNumber', sort: true},
    ];

    private group!: IObjectData;
    private readonly reqCreateOverride = new PromiseRequest('createoverride');
    private readonly reqDeleteOverride = new PromiseRequest('deleteoverride');
    private readonly reqRestoreOverride = new PromiseRequest('restoreoverride');
    private readonly reqUpdateOverrideComment = new PromiseRequest('updateoverridecomment');
    /** Filter-tab member list (searchobject by override user ids). */
    private readonly reqMemberSearchObject = new PromiseRequest('searchobject');
    /** One PromiseRequest per tuple search so in-flight calls are not coalesced (`PromiseRequest.promise`). */
    private readonly reqMemberUserTupleSearch = new PromiseRequest('searchobject');
    private readonly reqMemberManagerTupleSearch = new PromiseRequest('searchobject');
    private readonly reqDepartmentTupleSearch = new PromiseRequest('searchobject');
    private searchScheduleTimeoutId!: number;
    private departmentByOrgUnitnr: ReadonlyMap<string, TObjectData_basic> = new Map();
    private managerByReference: ReadonlyMap<string, TObjectData_basic> = new Map();
    /** Bumped on tab change and each new search request; stale responses are ignored. */
    private searchRequestCounter = 0;
    /** Dims the results grid during current-tab refresh (steps 1 and 6). */
    readonly isMemberResultsDimmed = ko.observable(false);
    /** Reused across search refreshes so expand/comment-edit state survives mutations. */
    private readonly memberRowByUserId = new Map<number, GroupMemberRowVm>();

    readonly memberRows: KnockoutComputed<GroupMemberRowVm[]>;
    readonly hasMemberRows: KnockoutComputed<boolean>;
    /** Total synced members for the current tab badge (from `searchgroupmember`). */
    readonly currentMemberTotal = ko.observable<number | null>(null);
    readonly tabOverrideCounts: KnockoutComputed<Record<'pending' | 'added' | 'removed', number>>;
    readonly tabBadges: KnockoutComputed<Record<TGroupMembersTab, string>>;
    readonly memberActionsEnabled = ko.pureComputed(() => !this.isRequesting());
    /** Per-tab `show all` preference, restored when switching member status tabs. */
    private readonly showAllByTab: Record<TGroupMembersTab, KnockoutObservable<boolean>>;

    constructor(
        params: IRouteDefinitionParams,
        private readonly membershipOverrides: KnockoutObservableArray<IOverride>,
        private readonly onMutationComplete: () => void | Promise<void>,
        private readonly memberTab: KnockoutObservable<TGroupMembersTab>,
    ) {
        super(params, false);
        this.serviceName = 'searchgroupmember';
        this.searchcolumn('GivenName');
        this.reqSearch = new PromiseRequest(this.serviceName);
        this.showAllByTab = {
            current: memoryStore.get(this.getShowAllStoreId('current'), SHOW_ALL_TAB_DEFAULTS.current),
            pending: memoryStore.get(this.getShowAllStoreId('pending'), SHOW_ALL_TAB_DEFAULTS.pending),
            added: memoryStore.get(this.getShowAllStoreId('added'), SHOW_ALL_TAB_DEFAULTS.added),
            removed: memoryStore.get(this.getShowAllStoreId('removed'), SHOW_ALL_TAB_DEFAULTS.removed),
        };
        this.kosub.subscribe(this.showAll, value => {
            this.showAllByTab[this.memberTab()](value);
        });
        this.showAll(this.showAllByTab[this.memberTab()]());

        this.memberRows = ko.pureComputed(() => this.resolveMemberRows());
        this.hasMemberRows = ko.pureComputed(() => this.memberRows().length > 0);
        this.tabOverrideCounts = ko.pureComputed(() => {
            const overrides = this.membershipOverrides();
            return {
                pending: filterOverridesForMembersTab(overrides, 'pending').length,
                added: filterOverridesForMembersTab(overrides, 'added').length,
                removed: filterOverridesForMembersTab(overrides, 'removed').length,
            };
        });
        this.tabBadges = ko.pureComputed(() => {
            const overrideCounts = this.tabOverrideCounts();
            return {
                current: this.formatTabBadgeCount(this.currentMemberTotal()),
                pending: this.formatTabBadgeCount(overrideCounts.pending),
                added: this.formatTabBadgeCount(overrideCounts.added),
                removed: this.formatTabBadgeCount(overrideCounts.removed),
            };
        });

        this.kosub.subscribe(this.memberTab, (tab: TGroupMembersTab) => {
            this.abortOngoingMemberSearch();
            this.beginMemberSearchRequest();
            this.searchpagenr(DEFAULT_SEARCH_PAGE);
            this.data([]);
            this.clearLookups();
            this.memberRowByUserId.clear();
            this.isMemberResultsDimmed(false);
            this.isRequesting(false);
            this.showAll(this.showAllByTab[tab]());
            this.preRequest();
        });
    }

    private getShowAllStoreId(tab: TGroupMembersTab): string {
        return `${this.__routeId}.group.members.showall.${tab}`;
    }

    /** Returns cached row VMs for the current page, pruning rows no longer visible. */
    private resolveMemberRows(): GroupMemberRowVm[] {
        const tab = this.memberTab();
        const overrides = this.membershipOverrides();
        const users = this.data().filter(user => userMatchesMembersTab(overrides, user, tab));
        const activeUserIds = new Set<number>();
        const rows: GroupMemberRowVm[] = [];

        for (const user of users) {
            if (user.id == null) {
                continue;
            }
            activeUserIds.add(user.id);
            let row = this.memberRowByUserId.get(user.id);
            if (!row) {
                row = new GroupMemberRowVm(user, this);
                this.memberRowByUserId.set(user.id, row);
            } else {
                row.setUser(user);
            }
            rows.push(row);
        }

        for (const id of Array.from(this.memberRowByUserId.keys())) {
            if (!activeUserIds.has(id)) {
                this.memberRowByUserId.delete(id);
            }
        }

        return rows;
    }

    setGroup(group: IObjectData): void {
        this.group = group;
        this.loadCurrentMemberTotal();
    }

    /** Fetches `totalresults` for the group without loading the full member list. */
    private loadCurrentMemberTotal(): void {
        if (!this.group?.id) {
            this.currentMemberTotal(null);
            return;
        }
        const req = new PromiseRequest('searchgroupmember');
        const groupId = this.group.id;
        void req.request(getSearchObjectParams({
            objectid: groupId,
            pagenr: 0,
            resultsprpage: 1,
            searchkey: '',
        }), true)
            .then((response: IResponse) => {
                if (this.group?.id === groupId) {
                    this.currentMemberTotal(response.totalresults ?? 0);
                }
            })
            .catch(() => {
                if (this.group?.id === groupId) {
                    this.currentMemberTotal(null);
                }
            })
            .finally(() => req.abort());
    }

    private formatTabBadgeCount(count: number | null): string {
        return count == null ? '' : toLocaleString(count);
    }

    getGroup(): IObjectData {
        return this.group;
    }

    /** Public accessor for host components (protected `isReady` on search base). */
    isMembersSearchReady(): boolean {
        return this.isReady() ?? false;
    }

    /** Whether a members search is active (`show all` or a non-empty search key). */
    private hasActiveMembersSearch(): boolean {
        return this.showAll() || !!this.searchkey();
    }

    /** Abort in-flight searchgroupmember/searchobject calls and pending search kick-offs. */
    private abortOngoingMemberSearch(): void {
        this.clearScheduledSearchRequest();
        clearTimeout(this.searchScheduleTimeoutId);
        this.reqSearch.abort();
        this.reqMemberSearchObject.abort();
        this.reqMemberUserTupleSearch.abort();
        this.reqMemberManagerTupleSearch.abort();
        this.reqDepartmentTupleSearch.abort();
    }

    /** Invalidate in-flight search callbacks and return the id for a new request. */
    private beginMemberSearchRequest(): number {
        return ++this.searchRequestCounter;
    }

    getOverrideForUser(user: IObjectData): IOverride | null {
        return findOverrideForUser(this.membershipOverrides(), user);
    }

    /** Plain row action (cancel / restore / remove) for one group member. */
    onMemberRowActionClick(user: IObjectData): void {
        this.onMemberActionClick(user);
    }

    getDepartmentForUser(user: IObjectData): TObjectData_basic | undefined {
        const orgUnitnr = getRawSyncFieldValue('DepartmentNumber', user.syncfields);
        if (!orgUnitnr || orgUnitnr === '0' || orgUnitnr === 'Root') {
            return undefined;
        }
        return this.departmentByOrgUnitnr.get(orgUnitnr);
    }

    getManagerForUser(user: IObjectData): TObjectData_basic | undefined {
        const managerRef = getRawSyncFieldValue('Manager', user.syncfields);
        if (!managerRef) {
            return undefined;
        }
        return this.managerByReference.get(managerRef);
    }

    getDepartmentDisplayText(user: IObjectData): string {
        const departmentName = getRawSyncFieldValue('Department', user.syncfields);
        if (departmentName) {
            return departmentName;
        }
        const department = this.getDepartmentForUser(user);
        return department ? getObjectDisplayName(department) ?? '' : '';
    }

    getManagerDisplayText(user: IObjectData): string {
        const manager = this.getManagerForUser(user);
        return manager ? getObjectDisplayName(manager) ?? '' : '';
    }

    onMemberActionClick(user: IObjectData): void {
        if (!this.group) {
            return;
        }
        const override = this.getOverrideForUser(user);
        if (override) {
            if (isOverridePendingDeletion(override)) {
                showCancelPendingDeletionConfirm(override, () =>
                    this.executeRestoreOverride(user, override));
                return;
            }
            if (!canMutateMembershipOverride(override)) {
                return;
            }
            showMembershipOverrideActionConfirm(override, () =>
                this.executeDeleteOverride(user, override));
            return;
        }
        nsGroupMembers.showManualMembershipEducationConfirm(() => this.executeRemoveMember(user));
    }

    private executeDeleteOverride(user: IObjectData, override: IOverride): void {
        // Reused rows keep expand state across refresh — collapse before restore.
        if (getMembershipOverrideAction(override).outcome === 'restore' && user.id != null) {
            this.memberRowByUserId.get(user.id)?.isExpanded(false);
        }
        this.runMemberMutation(callbacks =>
            runMembershipOverrideDelete(this.reqDeleteOverride, override, callbacks));
    }

    private executeRestoreOverride(user: IObjectData, override: IOverride): void {
        if (user.id != null) {
            this.memberRowByUserId.get(user.id)?.isExpanded(false);
        }
        this.runMemberMutation(callbacks =>
            runMembershipOverrideRestore(this.reqRestoreOverride, override, callbacks));
    }

    private executeRemoveMember(user: IObjectData): void {
        this.runMemberMutation(callbacks =>
            runMembershipRemoveMember(this.reqCreateOverride, this.group, user, callbacks));
    }

    /** Shared page-message and refresh flow for membership override mutations. */
    private runMemberMutation(
        run: (callbacks: IMembershipOverrideMutationCallbacks) => void,
    ): void {
        this.isRequesting(true);
        run({
            onStart: message => this.messageData({message}),
            onSuccess: () => {
                this.messageData(null);
                void this.completeOverrideMutation().finally(() => this.isRequesting(false));
            },
            onError: reason => {
                this.messageData(reason);
                this.isRequesting(false);
            },
            onFinally: () => {},
        });
    }

    private completeOverrideMutation(): Promise<void> {
        return Promise.resolve(this.onMutationComplete());
    }

    /** Persists override comment via `updateoverridecomment` and reloads group overrides. */
    updateOverrideComment(overrideId: number, comment: string): Promise<void> {
        return this.reqUpdateOverrideComment
            .request({id: overrideId, comment})
            .then(() => this.completeOverrideMutation())
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                return Promise.reject(reason);
            });
    }

    /** Refreshes the active member tab, using base search/show-all gating only for current members. */
    refreshMembers(): Promise<void> {
        if (!this.isReady()) {
            return Promise.resolve();
        }
        this.loadCurrentMemberTotal();
        this.preRequest();
        return Promise.resolve();
    }

    start(): Promise<void> {
        this.searchcolumn('GivenName');
        return this.init().then(() => this.preRequest());
    }

    protected override preRequest(): void {
        clearTimeout(this.searchScheduleTimeoutId);
        this.reqMemberSearchObject.abort();
        this.reqMemberUserTupleSearch.abort();
        this.reqMemberManagerTupleSearch.abort();
        this.reqDepartmentTupleSearch.abort();
        if (this.memberTab() === 'current') {
            super.preRequest();
            return;
        }
        if (!this.isReady()) {
            return;
        }
        this.reqSearch.abort();
        this.searchtotalresults(null);
        this.searchpagenr(DEFAULT_SEARCH_PAGE);
        this.searchpages(null);
        this.searchScheduleTimeoutId = window.setTimeout(() => this.request(), 100);
    }

    override request(): void {
        this.abortOngoingMemberSearch();
        const requestId = this.beginMemberSearchRequest();
        if (this.memberTab() === 'current') {
            void this.requestCurrentTabMembers(requestId);
            return;
        }
        this.requestFilterTabMembers(requestId);
    }

    /**
     * Current members tab — read top to bottom:
     * dim → searchgroupmember → user tuples → merge → manager tuples → department tuples → populate → undim
     */
    private async requestCurrentTabMembers(requestId: number): Promise<void> {
        const isStale = (): boolean => requestId !== this.searchRequestCounter;
        const requestTuples = (
            req: PromiseRequest,
            objectTypeId: ObjectTypeId,
            syncfieldNames: TSyncfield[],
            rules: IRule[],
            resultCount: number,
        ): Promise<TObjectData_basic[]> => {
            const pageSize = Math.min(Math.max(resultCount, 1), MAX_RESULTS_PER_PAGE);
            return getSyncfieldIdsFromNames(objectTypeId, ...syncfieldNames)
                .then(syncfieldids => req.request(getSearchObjectParams({
                    deleted: 0,
                    jruleset: JSON.stringify(generateRuleset({rules})),
                    objecttypeid: objectTypeId,
                    onlytuples: true,
                    resultsprpage: pageSize,
                    syncfieldids,
                })))
                .then((tuples: string[][]) =>
                    tuples.map(row => convertTuplesToObjectData(objectTypeId, syncfieldNames, row)),
                );
        };

        if (this.hasMemberRows()) {
            this.isMemberResultsDimmed(true);
        }
        this.isRequesting(true);

        try {
            const response: IResponse = await this.reqSearch.request(this.getParams(), true);
            if (isStale()) {
                return;
            }

            if (!response.data?.length) {
                this.clearLookups();
                if (this.hasActiveMembersSearch() && response.totalresults != null) {
                    this.messageData({message: nsSearch.MessageNoResult});
                    this.searchascending(response.sortascending);
                    this.searchcolumn(response.sortedby);
                    this.searchpagenr(response.pagenr ?? DEFAULT_SEARCH_PAGE);
                    this.searchpages(response.pages ?? null);
                    this.searchtotalresults(response.totalresults);
                    this.data([]);
                } else {
                    super.onSuccess(response);
                }
                return;
            }

            const members = response.data as IObjectData[];
            const userIds = members
                .map(o => o.id)
                .filter((id): id is number => id != null);

            const userTuples = await requestTuples(
                this.reqMemberUserTupleSearch,
                ObjectTypeId.User,
                [...MEMBER_CURRENT_TAB_USER_TUPLE_NAMES],
                [generateRule({
                    attribute: 'Id',
                    argumentoperator: 'Er lik en av',
                    argument: userIds.join(','),
                })],
                userIds.length,
            );
            if (isStale()) {
                return;
            }

            const enrichedById = new Map(
                userTuples.filter(u => u.id != null).map(u => [u.id as number, u]),
            );
            const users = members.map(member => {
                if (member.id == null) {
                    return member;
                }
                const enrichment = enrichedById.get(member.id);
                if (!enrichment?.syncfields?.length) {
                    return member;
                }
                const byName = new Map((member.syncfields ?? []).map(sf => [sf.name, sf]));
                for (const sf of enrichment.syncfields) {
                    const existing = byName.get(sf.name);
                    byName.set(sf.name, existing
                        ? _.defaultsDeep({value: sf.value}, existing)
                        : {name: sf.name, value: sf.value} as NonNullable<typeof existing>);
                }
                return {...member, syncfields: Array.from(byName.values())};
            });

            const managers: TObjectData_basic[] = [];
            const refsByFormat = collectManagerReferencesByImportFormat(users);
            for (const [importFormatId, managerRefs] of Array.from(refsByFormat.entries())) {
                const rules = buildManagerBatchRules(managerRefs, importFormatId);
                if (!rules.length) {
                    continue;
                }
                const batch = await requestTuples(
                    this.reqMemberManagerTupleSearch,
                    ObjectTypeId.User,
                    getManagerLookupSyncfieldNames(),
                    rules,
                    managerRefs.length,
                );
                managers.push(...batch);
            }
            if (isStale()) {
                return;
            }

            const orgUnitNumbers = collectDepartmentOrgUnitNumbers(users);
            const departmentRule = buildDepartmentBatchRule(orgUnitNumbers);
            const departments = departmentRule
                ? await requestTuples(
                    this.reqDepartmentTupleSearch,
                    ObjectTypeId.Department,
                    getDepartmentLookupSyncfieldNames(),
                    [departmentRule],
                    orgUnitNumbers.length,
                )
                : [];
            if (isStale()) {
                return;
            }

            this.departmentByOrgUnitnr = buildDepartmentLookupMap(departments);
            this.managerByReference = buildManagerLookupMap(managers);
            super.onSuccess(_.defaults({data: users}, response));
        } catch (reason: unknown) {
            if (isStale()) {
                return;
            }
            const err = reason as {lasterror?: string; message?: string};
            const message = typeof reason === 'string'
                ? reason
                : err?.lasterror || err?.message || String(reason);
            this.messageData(createErrorMessage(message));
        } finally {
            if (!isStale()) {
                this.isMemberResultsDimmed(false);
                this.isRequesting(false);
            }
        }
    }

    protected override getParams(): ISearchObjectParams {
        return super.getParams({
            objectid: global.getRouteId(),
        });
    }

    /** Pending / added / removed tabs: load override targets, filter, paginate, enrich, populate. */
    private requestFilterTabMembers(requestId: number): void {
        const tab = this.memberTab();
        const matchingOverrides = filterOverridesForMembersTab(this.membershipOverrides(), tab);
        const userIds = _.uniq(
            matchingOverrides
                .map(o => o.targetobjectid)
                .filter((id): id is number => id != null),
        );
        if (!userIds.length) {
            if (requestId !== this.searchRequestCounter) {
                return;
            }
            this.isRequesting(true);
            void this.finishFilterTabResponse([], 0, requestId);
            return;
        }
        this.isRequesting(true);
        getSyncfieldIdsFromNames(ObjectTypeId.User, ...MEMBER_ROW_SYNCFIELD_NAMES)
            .then(syncfieldids => this.reqMemberSearchObject.request(getSearchObjectParams({
                asc: this.searchascending(),
                deleted: 0,
                objecttypeid: ObjectTypeId.User,
                jruleset: JSON.stringify(generateRuleset({
                    rules: [generateRule({
                        attribute: 'Id',
                        argumentoperator: 'Er lik en av',
                        argument: userIds.join(','),
                    })],
                })),
                resultsprpage: userIds.length,
                sortcol: this.searchcolumn(),
                syncfieldids,
            })))
            .then((users: IObjectData[]) => {
                if (requestId !== this.searchRequestCounter) {
                    this.isRequesting(false);
                    return;
                }
                const overrides = this.membershipOverrides();
                let filtered = users.filter(user => userMatchesMembersTab(overrides, user, tab));
                const searchKey = this.getRealSearchKey();
                if (searchKey) {
                    filtered = filtered.filter(user => this.userMatchesSearchKey(user, searchKey));
                }
                return this.finishFilterTabResponse(filtered, filtered.length, requestId);
            })
            .catch((reason: string | {lasterror?: string}) => {
                if (requestId !== this.searchRequestCounter) {
                    return;
                }
                this.isRequesting(false);
                const message = typeof reason === 'string'
                    ? reason
                    : reason?.lasterror || String(reason);
                this.messageData(createErrorMessage(message));
            });
    }

    private async finishFilterTabResponse(
        allUsers: IObjectData[],
        total: number,
        requestId: number,
    ): Promise<void> {
        const isStale = (): boolean => requestId !== this.searchRequestCounter;
        if (isStale()) {
            return;
        }

        const requestTuples = (
            req: PromiseRequest,
            objectTypeId: ObjectTypeId,
            syncfieldNames: TSyncfield[],
            rules: IRule[],
            resultCount: number,
        ): Promise<TObjectData_basic[]> => {
            const pageSize = Math.min(Math.max(resultCount, 1), MAX_RESULTS_PER_PAGE);
            return getSyncfieldIdsFromNames(objectTypeId, ...syncfieldNames)
                .then(syncfieldids => req.request(getSearchObjectParams({
                    deleted: 0,
                    jruleset: JSON.stringify(generateRuleset({rules})),
                    objecttypeid: objectTypeId,
                    onlytuples: true,
                    resultsprpage: pageSize,
                    syncfieldids,
                })))
                .then((tuples: string[][]) =>
                    tuples.map(row => convertTuplesToObjectData(objectTypeId, syncfieldNames, row)),
                );
        };

        try {
            const pageSize = this.resultsPerPage();
            const page = this.searchpagenr();
            const pageUsers = allUsers.slice(page * pageSize, (page + 1) * pageSize);
            const pages = total ? Math.ceil(total / pageSize) : 0;

            if (!pageUsers.length) {
                this.clearLookups();
                super.onSuccess({
                    data: [],
                    pagenr: page,
                    pages,
                    totalresults: total,
                    sortascending: this.searchascending(),
                    sortedby: this.searchcolumn(),
                } as unknown as IResponse);
                return;
            }

            const orgUnitNumbers = collectDepartmentOrgUnitNumbers(pageUsers);
            const departmentRule = buildDepartmentBatchRule(orgUnitNumbers);
            const departments = departmentRule
                ? await requestTuples(
                    this.reqDepartmentTupleSearch,
                    ObjectTypeId.Department,
                    getDepartmentLookupSyncfieldNames(),
                    [departmentRule],
                    orgUnitNumbers.length,
                )
                : [];

            const managers: TObjectData_basic[] = [];
            const refsByFormat = collectManagerReferencesByImportFormat(pageUsers);
            for (const [importFormatId, managerRefs] of Array.from(refsByFormat.entries())) {
                const rules = buildManagerBatchRules(managerRefs, importFormatId);
                if (!rules.length) {
                    continue;
                }
                const batch = await requestTuples(
                    this.reqMemberManagerTupleSearch,
                    ObjectTypeId.User,
                    getManagerLookupSyncfieldNames(),
                    rules,
                    managerRefs.length,
                );
                managers.push(...batch);
            }

            if (isStale()) {
                return;
            }

            this.departmentByOrgUnitnr = buildDepartmentLookupMap(departments);
            this.managerByReference = buildManagerLookupMap(managers);
            super.onSuccess({
                data: pageUsers,
                pagenr: page,
                pages,
                totalresults: total,
                sortascending: this.searchascending(),
                sortedby: this.searchcolumn(),
            } as IResponse);
        } catch (reason: unknown) {
            if (isStale()) {
                return;
            }
            const err = reason as {lasterror?: string; message?: string};
            const message = typeof reason === 'string'
                ? reason
                : err?.lasterror || err?.message || String(reason);
            this.messageData(createErrorMessage(message));
        } finally {
            if (!isStale()) {
                this.isRequesting(false);
            }
        }
    }

    /** Text match for filter-tab client search (GivenName, Surname, EmployeeNumber). */
    private userMatchesSearchKey(user: IObjectData, searchKey: string): boolean {
        const pattern = new RegExp(searchKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
        return ['GivenName', 'Surname', 'EmployeeNumber'].some(key =>
            pattern.test(getRawSyncFieldValue(key as TSyncfield_User, user.syncfields) ?? ''),
        );
    }

    private clearLookups(): void {
        this.departmentByOrgUnitnr = new Map();
        this.managerByReference = new Map();
    }

    getSyncfieldValue(key: TSyncfield_User, data: IObjectData): string {
        switch (key) {
            case 'Id':
                return String(data.id);
            default:
                return getRawSyncFieldValue(key, data.syncfields) ?? '';
        }
    }

    override dispose(): void {
        super.dispose();
        this.abortOngoingMemberSearch();
        this.memberRowByUserId.clear();
        this.reqCreateOverride.abort();
        this.reqDeleteOverride.abort();
        this.reqRestoreOverride.abort();
        this.reqUpdateOverrideComment.abort();
        this.reqMemberUserTupleSearch.abort();
        this.reqMemberManagerTupleSearch.abort();
        this.reqDepartmentTupleSearch.abort();
    }
}
